import type { jsPDF } from "jspdf";
import type { SaleInvoice } from "./invoice.interface";
import { PDF_LOGO } from "./logo";
import { PDFGenerator } from "./pdf-generator";

type Options = {
  type: "sale" | "purchase";
  showDiscount?: boolean;
};

const DEFAULT_OPTIONS: Options = {
  type: "sale",
  showDiscount: true,
};

export class InvoiceGenerator {
  private pdf: PDFGenerator;
  private doc: jsPDF;
  private invoice: SaleInvoice;
  readonly pageWidth = 70;
  readonly pageHeight = 297;

  private options: Options;

  readonly fontSize = 8;
  readonly lineHeight = 5;
  readonly margin = 4;
  readonly titleFontSize = 14;
  readonly subTitleFontSize = 8;
  readonly fontFamily = "Helvetica";

  private logo = PDF_LOGO;

  private logoType = "JPG";

  private heightUsed = 0;

  constructor(invoice: SaleInvoice, options?: Options) {
    this.options = options || DEFAULT_OPTIONS;
    this.pdf = new PDFGenerator();
    this.doc = this.pdf.getDoc();
    this.pdf.setup();
    this.invoice = invoice;

    this.generate();
  }

  generate() {
    const payload = this.invoice;
    // this.addHeightUsed(this.margin / this.fontSize / 2);
    this.setTitle(this.invoice.store.name);
    this.setAddress(this.invoice.store.address, this.invoice.store.phone);
    this.setInvoiceNumber(payload.invoiceNumber);
    this.setDate(new Date());

    if (payload.customer) {
      this.setCustomerInfo({
        name: payload.customer.name,
        phone: payload.customer.phone || "N/A",
        email: payload.customer.email || "N/A",
      });
    }

    this.setSellerInfo(payload.sellerName);

    this.addItemsTable(payload.items);
    this.addLine();

    this.addTotals(
      payload.subTotalAmount,
      payload.tax,
      payload.totalAmount,
      payload.paidAmount,
      payload.dueAmount,
    );
    this.addFooter();
    this.setHeight();
  }

  getHeightUsed() {
    return this.heightUsed;
  }

  getImageType() {
    if (this.invoice.logo) {
      return this.invoice.logo.type;
    }
    return this.logoType;
  }

  getImageData() {
    if (this.invoice.logo) {
      return this.invoice.logo.image;
    }
    return this.logo;
  }

  getRequiredHeight(heightPoint: number, fontSize?: number) {
    const fsz = fontSize || this.fontSize;

    const multiplier = 1;

    // calculate required height based on font size
    const height = fsz * multiplier * heightPoint; // assuming 10 lines of text
    return height;
  }

  calculateRequiredHeight(height: number) {
    const requiredHeight = this.getRequiredHeight(height);

    return this.getHeightUsed() + requiredHeight;
  }

  addHeightUsed(height: number) {
    const requiredHeight = this.getRequiredHeight(height);
    this.heightUsed += requiredHeight;
  }

  private getDiscountLabelAndValue() {
    let discountLabel = "Discount";
    let discountValue = this.invoice.discount.toFixed(2);

    if (this.invoice.discountType === "percentage") {
      // discountLabel = `Discount (${this.invoice.discount.toFixed(2)}%)`;
      discountLabel = `Discount`;
      discountValue = (
        this.invoice.subTotalAmount -
        this.invoice.totalAmount -
        this.invoice.tax
      ).toFixed(2);
    }

    return { discountLabel, discountValue };
  }

  setTitle(title: string) {
    // const height = this.getRequiredHeight(2);
    this.doc.setFontSize(this.titleFontSize);

    // const imageHeight = this.titleFontSize;
    // const imageWidth = this.titleFontSize;
    // // const textWidth = this.doc.getTextWidth(title);
    //
    // // const imageX = (this.doc.internal.pageSize.width - textWidth) / 2 - imageWidth - 1;
    // const centerX = this.doc.internal.pageSize.width / 2 - imageWidth / 2;
    //
    const heightPoint = 1;
    // this.doc.addImage(
    //   this.getImageData(),
    //   this.getImageType(),
    //   centerX,
    //   1,
    //   imageWidth,
    //   imageHeight
    // );
    //
    // this.addHeightUsed(heightPoint * 1.3);

    this.doc.text(
      title,
      this.doc.internal.pageSize.width / 2,
      this.calculateRequiredHeight(heightPoint),
      {
        align: "center",
      },
    );

    this.addHeightUsed(heightPoint);
  }

  setAddress(address: string, phoneNumber: string) {
    const heightPoint = 0.5;

    this.doc.setFontSize(this.subTitleFontSize);
    this.doc.text(
      address,
      this.doc.internal.pageSize.width / 2,
      this.getHeightUsed() + this.getRequiredHeight(heightPoint),
      {
        align: "center",
      },
    );

    this.addHeightUsed(heightPoint);

    this.doc.text(
      `Phone: ${phoneNumber}`,
      this.doc.internal.pageSize.width / 2,
      this.getHeightUsed() + this.getRequiredHeight(heightPoint),
      {
        align: "center",
      },
    );

    this.addHeightUsed(heightPoint);
  }

  setInvoiceNumber(invoiceNumber: string) {
    const heightPoint = 0.7;

    const text =
      this.options.type === "sale" ? `Sales Receipt` : `Purchase Receipt`;

    this.doc.setFontSize(this.fontSize);
    this.doc.setFont(this.fontFamily, "bold");
    this.doc.text(
      text,
      this.doc.internal.pageSize.width / 2,
      this.calculateRequiredHeight(heightPoint),
      {
        align: "center",
      },
    );
    this.doc.setFontSize(this.fontSize);
    this.doc.setFont(this.fontFamily, "normal");

    this.doc.text(
      `${invoiceNumber}`,
      this.doc.internal.pageSize.width / 2,
      this.calculateRequiredHeight(heightPoint + heightPoint * 0.7),
      {
        align: "center",
      },
    );
    this.addHeightUsed(heightPoint + heightPoint * 0.7);
  }

  setDate(date: Date) {
    const heightPoint = 0.8;

    this.doc.setFontSize(this.fontSize);
    // const y = this.heightUsed + 5;
    const dateStr = `Date: ${date.toLocaleDateString("en-US")}`;
    const timeStr = `Time: ${date.toLocaleTimeString("en-US")}`;
    this.doc.text(
      dateStr,
      this.margin,
      this.calculateRequiredHeight(heightPoint),
    );
    this.doc.text(
      timeStr,
      this.doc.internal.pageSize.width - this.margin,
      this.calculateRequiredHeight(heightPoint),
      {
        align: "right",
      },
    );
    this.addHeightUsed(heightPoint);
  }

  addLine() {
    // Set 50% stroke opacity if supported
    // const gState = (this.doc as any).addGState?.({ opacity: 0.5, strokeOpacity: 0.5 });
    // if (gState) {
    //     (this.doc as any).setGState(gState);
    // } else {
    // Fallback: use a lighter gray to mimic reduced opacity
    this.doc.setDrawColor(150);
    // }

    this.doc.setLineWidth(0.1);
    this.doc.line(
      this.margin,
      this.heightUsed + 3,
      this.doc.internal.pageSize.width - this.margin,
      this.heightUsed + 3,
    );

    this.doc.setDrawColor(0); // Reset to black

    // Reset opacity if we changed it
    // if (gState) {
    //     const reset = (this.doc as any).addGState?.({ opacity: 1, strokeOpacity: 1 });
    //     if (reset) (this.doc as any).setGState(reset);
    // } else {
    //     // Reset draw color to black
    //     this.doc.setDrawColor(0);
    // }

    this.heightUsed += 3;
  }

  // Store metadata about last customer block for positioning seller info
  setCustomerInfo(customer: { name: string; phone: string; email: string }) {
    const heightPoint = 1.2;

    this.doc.setFontSize(this.fontSize);
    const startY = this.calculateRequiredHeight(heightPoint / 2);

    const line1 = `${this.options.type === "sale" ? "Customer" : "Supplier"} ID: ${this.invoice.customer?.id || "N/A"}`;
    const line2 = `${this.options.type === "sale" ? "Customer" : "Supplier"} Name: ${customer.name}`;

    this.doc.text(
      line1,
      this.margin,
      this.calculateRequiredHeight(heightPoint / 2),
    );
    this.doc.text(
      line2,
      this.margin,
      this.calculateRequiredHeight(heightPoint),
    );

    const nameWidth = this.doc.getTextWidth(line1);
    const phoneWidth = this.doc.getTextWidth(line2);
    const maxLineWidth = Math.max(nameWidth, phoneWidth);

    // Reserve vertical space (might be extended if seller needs an extra line)
    // this.heightUsed += 15;
    this.addHeightUsed(heightPoint);

    // Stash layout info for seller
    (this as any)._lastCustomerBlock = {
      startY,
      maxLineWidth,
    };
  }

  // Places seller name to the right of customer details without overlap
  setSellerInfo(name: string) {
    this.doc.setFontSize(this.fontSize);
    const meta = (this as any)._lastCustomerBlock;

    const heightPoint = 0.5;

    if (!meta) {
      // Fallback: no customer info printed yet

      const text = this.options.type === "sale" ? `Served by:` : "Received by:";

      this.doc.text(
        text,
        this.margin,
        this.calculateRequiredHeight(heightPoint),
      );
      this.doc.text(
        `${name}`,
        this.margin,
        this.calculateRequiredHeight(heightPoint * 2),
      );
      this.addHeightUsed(heightPoint * 2);
      return;
    }

    const sellerText =
      this.options.type === "sale" ? `Served By:` : "Received By:";
    const sellerName = name || "N/A";
    const sellerWidth = this.doc.getTextWidth(sellerText);
    const pageWidth = this.doc.internal.pageSize.width;
    const rightX = pageWidth - this.margin;

    // Attempt to place on the same first line aligned right
    const sellerLeftX = rightX - sellerWidth;
    const minAllowedX = this.margin + meta.maxLineWidth + 4; // 4 = small gap

    let y = meta.startY;
    let placedBelow = false;

    if (sellerLeftX <= minAllowedX) {
      // Overlaps; move below customer block
      y = meta.startY + 5; // below the two customer lines
      placedBelow = true;
    }

    this.doc.text(sellerText, rightX, y, { align: "right" });
    this.doc.text(sellerName, rightX, y + 5, { align: "right" });

    if (placedBelow) {
      // Ensure extra vertical space if we extended block
      const neededBottom = y + 5; // baseline + line height space
      const currentBottom = this.heightUsed - 15 + 15; // original reserved bottom
      if (neededBottom > currentBottom) {
        this.heightUsed += neededBottom - currentBottom;
      }
    }
  }

  addItemsTable(items: SaleInvoice["items"]) {
    const heightPoint = 1;

    const tableData = items.map((item, _i) => [
      item.productName,
      item.price.toFixed(2),
      item.quantity.toString(),
      `${(item.price * item.quantity).toFixed(2)}`,
    ]);

    (this.doc as any).autoTable({
      startY: this.calculateRequiredHeight(heightPoint),
      head: [[`Items (${items.length})`, "Price", "Qty", "Total"]],
      body: tableData,
      theme: "plain",
      // tableLineColor: 200,

      styles: {
        font: this.fontFamily,
        fontSize: this.fontSize,

        // Remove all default borders
        lineWidth: 0,
        overflow: "linebreak",
        cellPadding: 1,
        fontStyle: "normal",
      },
      headStyles: {
        fillColor: [255, 255, 255],
        lineColor: [100, 100, 100],
        textColor: [0, 0, 0],
        fontSize: this.fontSize,
        fontStyle: "bold",
        halign: "right",
        valign: "middle",
        // We'll custom draw borders so keep lineWidth 0 here
        lineWidth: 0,
      },
      margin: { left: this.margin },
      tableWidth: this.pageWidth - 2 * this.margin,

      columnStyles: {
        0: {
          cellWidth: this.calculateTableWidth(50),
          cellPadding: {
            top: this.fontSize * 0.1,
            bottom: this.fontSize * 0.1,
            left: 0,
            right: 0,
          },
          overflow: "linebreak",
          halign: "left",
        }, // Item
        1: {
          cellWidth: this.calculateTableWidth(20),
          cellPadding: {
            top: this.fontSize * 0.1,
            bottom: this.fontSize * 0.1,
            left: 0,
            right: 0,
          },
          halign: "right",
        }, // Price
        2: {
          cellWidth: this.calculateTableWidth(10),
          cellPadding: {
            top: this.fontSize * 0.1,
            bottom: this.fontSize * 0.1,
            left: 0,
            right: 0,
          },
          halign: "right",
        }, // Qty
        3: {
          cellWidth: this.calculateTableWidth(20),
          cellPadding: {
            top: this.fontSize * 0.1,
            bottom: this.fontSize * 0.1,
            left: 0,
            right: 0,
          },
          halign: "right",
        }, // Total Price
      },
      didDrawCell: (data: any) => {
        if (data.section === "head") {
          const { cell, doc } = data;
          const { x, y, width, height } = cell;
          // Draw a rectangle border around each header cell
          doc.setLineWidth(0.1);
          doc.rect(x, y, width, height);
        }
      },
      didParseCell: (data: any) => {
        // Make first header cell left-aligned
        if (data.section === "head" && data.column.index === 0) {
          data.cell.styles.halign = "left";
        }
      },
    });

    this.heightUsed = (this.doc as any).lastAutoTable.finalY;
  }

  calculateTableWidth(percentage: number) {
    return (
      (this.doc.internal.pageSize.width - 2 * this.margin) * (percentage / 100)
    );
  }

  addTotals(
    subTotal: number,
    tax: number,
    total: number,
    paid: number,
    due: number,
  ) {
    if (this.options.type === "sale") {
      const heightPoint = 0.4;
      const startY = this.calculateRequiredHeight(heightPoint);
      const lineGap = this.lineHeight; // reuse configured line height
      const labelX = this.margin;
      const valueX = this.doc.internal.pageSize.width - this.margin;

      const paymentMethod = this.invoice.paymentMethod || "CASH";
      // const discount =
      //   this.invoice.discountType === "percentage"
      //     ? `(${this.invoice.discount.toFixed(2)}%)`
      //     : `${this.invoice.discount.toFixed(2)}`;

      const { discountLabel, discountValue } = this.getDiscountLabelAndValue();

      type Rows = { label: string; value: string; bold?: boolean }[];

      const rows: Rows = [
        { label: "Subtotal", value: subTotal.toFixed(2) },
        { label: "VAT", value: tax.toFixed(2) },
      ];

      if (this.options.showDiscount) {
        rows.push({ label: discountLabel, value: discountValue });
        rows.push({ label: "Net Amount", value: total.toFixed(2), bold: true });
        rows.push({ label: "Paid Amount", value: paid.toFixed(2) });
      }

      if (due > 0) {
        rows.push({ label: "Due", value: due.toFixed(2) });
      }

      rows.push({
        label: "Payment Method",
        value: paymentMethod?.toUpperCase(),
      });

      let y = startY;

      rows.forEach((r) => {
        this.doc.setFont(this.fontFamily, r.bold ? "bold" : "normal");
        this.doc.setFontSize(this.fontSize);
        // Label (left)
        this.doc.text(r.label, labelX, y);
        // Value (right aligned to printable area)
        this.doc.text(r.value, valueX, y, { align: "right" });
        y += lineGap;
      });

      this.heightUsed = y;
    } else {
      const heightPoint = 0.4;
      const startY = this.calculateRequiredHeight(heightPoint);
      const lineGap = this.lineHeight; // reuse configured line height
      const labelX = this.margin;
      const valueX = this.doc.internal.pageSize.width - this.margin;

      const paymentMethod = this.invoice.paymentMethod || "CASH";

      const rows: { label: string; value: string; bold?: boolean }[] = [
        { label: "Subtotal", value: subTotal.toFixed(2) },
        { label: "VAT", value: tax.toFixed(2) },
        { label: "Net Amount", value: total.toFixed(2), bold: true },
        { label: "Paid Amount", value: paid.toFixed(2) },
        { label: "Payment Method", value: paymentMethod?.toUpperCase() },
        // { label: "Due", value: due.toFixed(2) },
      ];

      let y = startY;

      rows.forEach((r) => {
        this.doc.setFont(this.fontFamily, r.bold ? "bold" : "normal");
        this.doc.setFontSize(this.fontSize);
        // Label (left)
        this.doc.text(r.label, labelX, y);
        // Value (right aligned to printable area)
        this.doc.text(r.value, valueX, y, { align: "right" });
        y += lineGap;
      });

      this.heightUsed = y;
    }
  }

  addFooter() {
    this.addFooterNote();

    if (this.invoice.store.thanksMessage) {
      this.addThanksMessage();
    }
  }

  addThanksMessage() {
    if (!this.invoice.store.thanksMessage) {
      return;
    }

    const heightPoint = 0.5;

    this.doc.setFontSize(this.fontSize);
    this.doc.text(
      this.invoice.store.thanksMessage,
      this.doc.internal.pageSize.width / 2,
      this.calculateRequiredHeight(heightPoint),
      { align: "center" },
    );

    this.addHeightUsed(heightPoint);
  }

  addFooterNote() {
    const note = this.invoice.store.footerNotes;
    if (!note) return;

    const heightPoint = 5;

    this.doc.setFontSize(this.fontSize - 2);

    const splitNote = this.doc.splitTextToSize(
      note,
      this.doc.internal.pageSize.width - 2 * this.margin,
    );
    this.doc.text(splitNote, this.margin, this.heightUsed + heightPoint, {
      align: "left",
    });
    this.heightUsed += splitNote.length * (this.fontSize - 2) * 0.35;

    this.heightUsed += heightPoint;
  }

  setHeight() {
    // const finalHeight = (this.doc.internal as any).getNumberOfPages() * this.doc.internal.pageSize.getHeight();
    // this.doc.internal.pageSize.height = finalHeight;
    // const pageSize = this.doc.internal.pageSize;
    // pageSize.height = this.heightUsed + this.margin + 10;
  }

  download() {
    this.pdf.download("invoice.pdf");
  }

  print() {
    this.pdf.print();
  }
}
